import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Autocomplete, Box, CircularProgress, Typography } from '@mui/material';
import { searchLocations } from '../../data/cities';

// Define the structure for location search results
export interface LocationResult {
   placeId?: string;
   description: string; // Full formatted address
   mainText: string; // Primary name of the location
   secondaryText: string; // Secondary address details
   coordinates: {
      lat: number;
      lng: number;
   };
}

// Props for the LocationSearch component
interface LocationSearchProps {
  label?: string;
  placeholder?: string;
  value?: LocationResult | null;
  onChange: (location: LocationResult | null) => void;
  onError?: (error: Error) => void;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  types?: string[]; // Optional filter for place types (retained for API compatibility)
}

const LocationSearch: React.FC<LocationSearchProps> = ({
  label = 'Location',
  placeholder = 'Search for a location',
  value = null,
  onChange,
  onError,
  required = false,
  disabled = false,
  fullWidth = true,
  size = 'medium',
  types, // kept for compatibility
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationResult[]>([]);
  const [loading, setLoading] = useState(false);

  // Get place suggestions from static data
  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input) {
        setOptions([]);
        return;
      }

      setLoading(true);
      
      try {
        // Simulate network delay for a more realistic experience
        setTimeout(() => {
          // Filter cities based on input
          const results = searchLocations(input);
          setOptions(results);
          setLoading(false);
        }, 300);
      } catch (error) {
        setLoading(false);
        if (onError && error instanceof Error) {
          onError(error);
        }
      }
    },
    [onError]
  );

  // Debounce input to mimic API behavior
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue, fetchSuggestions]);

  // Handle selection of a place
  const handlePlaceSelect = (
    _: React.SyntheticEvent,
    option: LocationResult | null
  ) => {
    onChange(option);
  };

  return (
    <Autocomplete
      id="location-search"
      fullWidth={fullWidth}
      disabled={disabled}
      options={options}
      loading={loading}
      value={value}
      onChange={handlePlaceSelect}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      getOptionLabel={(option) => option.description}
      filterOptions={(x) => x} // We're handling filtering in the searchCities function
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          size={size}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(optionProps, option) => {
        // Extract key from props
        const { key, ...restProps } = optionProps as any;
        
        return (
          <Box component="li" key={key} {...restProps}>
            <Box>
              <Typography variant="body1" component="div">
                {option.mainText}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {option.secondaryText}
              </Typography>
            </Box>
          </Box>
        );
      }}
    />
  );
};

export default LocationSearch; 